/**
 * CsatThanksPage.tsx — visual refresh v2 (side/csat-visual-refresh-v2).
 *
 * Owns: the post-rating landing page that the /api/csat/rate redirect
 *       lands on (`/csat-thanks?token=...&rating=N`). User has already
 *       picked a star; this page captures their optional comment.
 *
 * v2 visual brief (matches the refreshed email):
 *   · Outer viewport: Carbon Black (#191919) — pushes the card forward.
 *   · Card: Platinum (#f2f2f2), 560px max-width, flat 1px border.
 *   · Logo: black-on-light wordmark (/logos/bsg-black.png), centered, 140px.
 *   · Title: Carbon Black, 24/600, head font.
 *   · Rating echo: body copy with the number in Spicy Paprika #f76100.
 *   · TextField: focus border Electric Blue #0443ef, no glow.
 *   · Submit: Electric Blue bg + Platinum text + head font (matches the
 *     app's button.fontFamily token). Hover darkens to #0335c2.
 *
 * Behavior preserved verbatim: URL parsing, disabled-while-empty submit,
 * POST to /api/csat/feedback, success state. Only the visual treatment
 * + the logo src changed.
 */

import React from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';

// BSG brand tokens — kept inline rather than threaded through the MUI
// theme, since this page is rendered outside the authed app shell and
// shouldn't be coupled to the runtime theme. Hexes mirror
// frontend/src/theme/cssVariables.ts.
const BRAND = {
  ink: 'var(--ink)',          // Carbon Black — outer viewport + headings
  platinum: '#f2f2f2',     // Platinum — card surface + button text
  border: 'var(--rule)',
  mute: 'var(--ink-faint)',
  faint: 'var(--ink-faint)',
  paprika: 'var(--paprika)',      // Spicy Paprika — rating echo accent
  indigo: 'var(--indigo)',       // Electric Blue — primary CTA + focus
  indigoHover: '#0335c2',
};

// Font stacks mirrored from frontend/src/theme.ts (FONT_HEAD / FONT_BODY).
const FONT_HEAD = "'Inter Tight', 'Acumin Variable Concept', 'Codec Cold Trial', 'Roboto Condensed', 'Helvetica Neue', Arial, sans-serif";
const FONT_BODY = "'Inter', 'Manrope', 'Satoshi', 'Helvetica Neue', Arial, sans-serif";

const CsatThanksPage: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const rating = Number(params.get('rating') || 0);
  const token = params.get('token') || '';
  const [comment, setComment] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmitComment = async () => {
    if (!comment.trim() || !token) return;
    setSubmitting(true);
    try {
      await fetch('/api/csat/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, rating, comment }),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
    setSubmitting(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: BRAND.ink,
        fontFamily: FONT_BODY,
        px: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: 560,
          width: '100%',
          mx: 'auto',
          bgcolor: BRAND.platinum,
          border: `1px solid ${BRAND.border}`,
          boxShadow: 'none',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        {/* Logo — black-on-light, centered, 140px wide. */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: '36px',
          }}
        >
          <img
            src="/logos/bsg-black.png"
            alt="Black Sheep Global"
            style={{ width: 140, maxWidth: 140, height: 'auto', display: 'block' }}
          />
        </Box>

        {/* Body — title + rating echo + form. */}
        <Box sx={{ px: '40px', pb: '40px', textAlign: 'center' }}>
          <Typography
            component="h1"
            sx={{
              color: BRAND.ink,
              fontSize: '26px',
              fontWeight: 600,
              lineHeight: 1.25,
              mb: '12px',
              fontFamily: FONT_HEAD,
            }}
          >
            Thank you for your feedback!
          </Typography>

          <Typography
            sx={{
              color: BRAND.ink,
              fontSize: '14px',
              lineHeight: '24px',
              mb: '32px',
              fontFamily: FONT_BODY,
            }}
          >
            You rated our support{' '}
            <Box component="strong" sx={{ color: BRAND.paprika, fontWeight: 700, fontFamily: FONT_HEAD }}>
              {rating} out of 5
            </Box>
            .
          </Typography>

          {!submitted ? (
            <>
              <TextField
                multiline
                rows={5}
                fullWidth
                placeholder="Any additional comments? (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                sx={{
                  mb: '24px',
                  '& .MuiOutlinedInput-root': {
                    fontFamily: FONT_BODY,
                    fontSize: '14px',
                    borderRadius: '4px',
                    bgcolor: 'var(--paper)',
                    '& fieldset': { borderColor: BRAND.border, borderWidth: '1px' },
                    '&:hover fieldset': { borderColor: BRAND.border },
                    '&.Mui-focused fieldset': { borderColor: BRAND.indigo, borderWidth: '1px', boxShadow: 'none' },
                  },
                  '& .MuiOutlinedInput-input': { padding: '12px 14px', minHeight: '116px' },
                }}
                inputProps={{ maxLength: 2000 }}
              />
              <Button
                variant="contained"
                onClick={handleSubmitComment}
                disabled={submitting || !comment.trim()}
                disableElevation
                sx={{
                  bgcolor: BRAND.indigo,
                  color: BRAND.platinum,
                  fontFamily: FONT_HEAD,
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '4px',
                  px: '28px',
                  py: '12px',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: BRAND.indigoHover, boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: BRAND.faint, color: BRAND.platinum },
                }}
              >
                {submitting ? 'Submitting…' : 'Submit Comment'}
              </Button>
            </>
          ) : (
            <Typography
              sx={{
                color: BRAND.indigo,
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: FONT_HEAD,
              }}
            >
              Comment submitted. Thank you!
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CsatThanksPage;
